<script setup>
import { computed, reactive, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required, maxLength } from '@vuelidate/validators';
import { useStoreGetters } from 'dashboard/composables/store';
import { useWgptAuditTrail } from 'dashboard/composables/wgpt/wgptAuditTrail';
import { alphanumericUnderscore } from 'dashboard/routes/dashboard/settings/wgptBots/edit/tools/helpers/rulesHelper';
import { useI18n } from 'vue-i18n';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import AuditTrails from './ToolEditorAuditTrails.vue';

const props = defineProps({
  server: {
    type: Object,
    default: () => ({}),
  },
  tool: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const getters = useStoreGetters();
const { isFeatureEnabled } = useWgptAuditTrail();

const tools = computed(() =>
  getters['wgptMcpServers/getTools'].value(props.server.id)
);

const isUniqueName = value => {
  if (!value) return true;
  return !tools.value.some(
    tool => tool.name === value && tool.id !== props.tool.id
  );
};

const rules = {
  name: {
    required,
    alphanumericUnderscore,
    maxLength: maxLength(63),
    isUnique: helpers.withMessage(
      () =>
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.ERROR_UNIQUE'),
      isUniqueName
    ),
  },
  description: {
    required,
    maxLength: maxLength(1024),
  },
};

const state = reactive({
  name: props.modelValue.name || '',
  description: props.modelValue.description || '',
});

const v$ = useVuelidate(rules, state);

const errorMessage = computed(() => {
  if (!v$.value.name.$error) return '';
  if (v$.value.name.alphanumericUnderscore.$invalid) {
    return t(
      'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.ERROR_ALPHANUMERIC'
    );
  }
  if (v$.value.name.maxLength.$invalid) {
    return t(
      'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.ERROR_MAX_LENGTH'
    );
  }
  if (v$.value.name.isUnique.$invalid) {
    return t(
      'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.ERROR_UNIQUE'
    );
  }
  return t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.ERROR');
});

const descriptionErrorMessage = computed(() => {
  if (!v$.value.description.$error) return '';
  if (v$.value.description.maxLength.$invalid) {
    return t(
      'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.DESCRIPTION.ERROR_MAX_LENGTH'
    );
  }
  return t(
    'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.DESCRIPTION.ERROR'
  );
});

watch(
  state,
  newValue => {
    emit('update:modelValue', { ...props.modelValue, ...newValue });
  },
  { deep: true }
);

const onSubmit = () => {
  v$.value.$validate();
};
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <div class="flex flex-col gap-2">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        <span class="truncate">
          {{ $t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.TITLE') }}
        </span>
      </h3>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
      <Input
        v-model="state.name"
        :disabled="disabled"
        :label="
          $t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.LABEL')
        "
        :message="errorMessage"
        :message-type="errorMessage ? 'error' : 'info'"
        @input="v$.name.$touch"
        @blur="onSubmit"
      />

      <TextArea
        v-model="state.description"
        custom-text-area-class="!outline !outline-0"
        :disabled="disabled"
        :label="
          t(
            'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.DESCRIPTION.LABEL'
          )
        "
        :message="descriptionErrorMessage"
        :message-type="descriptionErrorMessage ? 'error' : 'info'"
        @input="v$.description.$touch"
        @blur="onSubmit"
      />
    </form>

    <template v-if="isFeatureEnabled">
      <div class="border-t border-n-weak my-4" />

      <AuditTrails />
    </template>
  </div>
</template>
